/* eslint-disable react/no-children-prop */
import { useEffect, useState } from 'react';
import { Button, DrawerHeader, Text } from 'components/styleguide';
import { useRootTranslation } from 'utils/hooks';
import { Divider, Grid } from '@material-ui/core';
import { Trans } from 'react-i18next';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { openPrompt } from 'redux-core/prompt/actions';
import { useDispatch } from 'react-redux';
import {
  checkImportFileCall,
  processImportFileCall,
  validateImportFileCall,
} from 'redux-core/import-tracks-spreadsheet/services';
import enhancer from './enhancer';
import FileDropZone from './FileDropZone';
import ErrorOnSpreadsheet from './ErrorOnSpreadsheet';
import ReadyToImport from './ReadyToImport';
import { processingPrompt } from './ProcessingPrompt';
import { ongoingImportPrompt } from './OngoingImportPrompt';

const rootT = 'drawers.importTracksSpreadsheetDrawer';

const FormatedLiTagComponent = ({ children }) =>
(
  <li>
    <Text
      variant="formLabel"
      children={children}
    />
  </li>
);

const ImportTracksSpreadsheetDrawer = ({
  onClose,
  getTemplateFile,
  currentUserId,
  productionId,
  divisionId,
  ...props
}) => {
  const cueContainerId = props.payload.id;
  const t = useRootTranslation(rootT);
  const dispatch = useDispatch();

  const [importJobId, setImportJobId] = useState(null);
  const [readyImport, setReadyImport] = useState(null);
  const [s3FileKey, setS3FileKey] = useState(null);
  const [validSpreadsheet, setValidSpreadsheet] = useState({
    state: null,
    error: null,
  });

  useEffect(() => {
    const ongoingImportCheck = async () => {
      const haveAnOngoingImport = await checkImportFileCall({ productionId });
      if (haveAnOngoingImport) {
        await dispatch(openPrompt(ongoingImportPrompt(t)));
        onClose();
      }
    };
    ongoingImportCheck();
  }, [productionId, t, dispatch, onClose]);

  const downloadTemplate = async () => {
    await getTemplateFile();
  };

  const importTracks = async () => {
    // Call to backend to import tracks file
    if (importJobId) {
      processImportFileCall({ jobId: importJobId, divisionId });
      await dispatch(openPrompt(processingPrompt(t)));
      onClose();
    }
  };

  useEffect(() => {
    const validate = async () => {
      const { status, statusReason, id: jobId } = await validateImportFileCall({
        s3FileKey,
        userId: currentUserId,
        productionId,
        cueContainerId,
      });

      const isValid = status === 'FILE_STRUCTURE_VALID';

      setReadyImport(isValid);

      setValidSpreadsheet({
        error: statusReason,
        state: isValid,
      });

      if (isValid) {
        setImportJobId(jobId);
      }
    };

    if (s3FileKey !== null) {
      // Call backend to validate file
      validate();
    }
  }, [s3FileKey, cueContainerId, currentUserId, productionId]);

  return (
    <>
      <DrawerHeader bold hideBackButton onClose={onClose} title={t('title')}>
        <Button
          alwaysEnabled
          children={t('cancel')}
          onClick={onClose}
          size="xs"
          synchronous
          testId="save-as-template-cancel"
          variant="destructive"
        />
        <Button
          children={t('import')}
          disabled={!readyImport}
          onClick={importTracks}
          size="sm"
          testId="save-as-template-save"
          type="submit"
          variant="primary"
        />
      </DrawerHeader>
      <Grid container spacing={2}>
        <Divider sx={{ my: 2 }} />
        <Grid item xs={9}>
          <Text
            children={t('apresentation')}
          />
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            endIcon={<FileDownloadIcon />}
            size="sm"
            variant="text"
            children={t('downloadTemplate')}
            onClick={downloadTemplate}
            type="button"
          />
        </Grid>
        <Grid container direction="column">
          <Text
            children={t('steps.title')}
            bold
          />
          <Grid item>
            <Trans
              i18nKey={`${rootT}.steps.step1`}
            >
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
            </Trans>
          </Grid>
          <Grid item>
            <Trans
              i18nKey={`${rootT}.steps.step2`}
            >
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
              <ul style={{ marginTop: 0, marginBottom: 8 }}>
                <FormatedLiTagComponent />
                <FormatedLiTagComponent />
              </ul>
            </Trans>
          </Grid>
          <Grid item>
            <Trans
              i18nKey={`${rootT}.steps.step3`}
            >
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
            </Trans>
          </Grid>
        </Grid>
      </Grid>
      {
        readyImport === null && (
          <FileDropZone
            setS3FileKey={setS3FileKey}
          />
        )
      }
      {
        readyImport && (<ReadyToImport />)
      }
      {
        (
          readyImport === false &&
          validSpreadsheet.state === false
        ) && (
          <ErrorOnSpreadsheet
            error={validSpreadsheet.error}
            setReadyImport={setReadyImport}
            setValidSpreadsheet={setValidSpreadsheet}
          />
        )
      }
    </>
  );
};

export default enhancer(ImportTracksSpreadsheetDrawer);
